import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconPlus } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { useOpuList } from '../contexts/opu';
import { useSessionActions } from '../contexts/actions';
import SessionTimeline from './session-timeline';
import OpuActionSearch from './action-search';
import SectionTabs from '../../navigation/section-tabs';
import { OPU_TABS } from '../../navigation/tabs';

import './styles/overview.css';
import '../../../common/styles/catalog.css';

// Aspirador = lead_vet staff. NOTE: not present in view=summary yet — shows
// '—' until the backend adds staff/lead_vet to the list summary.
function aspiradorName(s) {
    const lead = (s.staff || []).find(x => x.role === 'lead_vet' && x.status !== 0);
    return lead?.user?.name || null;
}

export default function OpuOverview() {
    const [params, setParams] = useSearchParams();
    const from = params.get('from') || '';
    const to   = params.get('to') || '';
    const runStep = useSessionActions();

    // Calendar dates from <input type="date"> are 'YYYY-MM-DD' wall-clock —
    // sent verbatim to from/to, no TZ conversion (decisions/0004).
    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next, { replace: true });
    };

    const clearFilters = () => setParams(new URLSearchParams(), { replace: true });

    const filters = useMemo(() => {
        const out = {};
        if (from) out.from = from;
        if (to)   out.to = to;
        return out;
    }, [from, to]);

    const { data, isLoading, isFetching, error } = useOpuList(filters);
    const sessions = data?.items ?? [];
    const total = data?.pagination?.total ?? sessions.length;
    const hasFilters = !!(from || to);

    return (
        <div className="cat-overview">
            <SectionTabs tabs={OPU_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Sesiones OPU</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="aspirations" action="write">
                    <Link to="/admin/opu/new" className="cat-overview__add">
                        <IconPlus />
                        <span>Nueva OPU</span>
                    </Link>
                </Gate>
            </header>

            <OpuActionSearch />

            <div className="cat-overview__toolbar opu-toolbar">
                <div className="opu-toolbar__dates">
                    <label className="opu-toolbar__date">
                        <span>Desde</span>
                        <input type="date" value={from} max={to || undefined} onChange={(e) => setParam('from', e.target.value)} />
                    </label>
                    <label className="opu-toolbar__date">
                        <span>Hasta</span>
                        <input type="date" value={to} min={from || undefined} onChange={(e) => setParam('to', e.target.value)} />
                    </label>
                </div>
                {hasFilters && (
                    <button type="button" className="cat-overview__clear" onClick={clearFilters}>Limpiar</button>
                )}
            </div>

            {error && <div className="cat-overview__error">{error.message || 'No se pudieron cargar las sesiones'}</div>}
            {total > sessions.length && <p className="cat-overview__note">Mostrando las primeras {sessions.length} de {total} sesiones. Acotá por fecha o filtros.</p>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando sesiones…</div>
            ) : !sessions.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">
                        {hasFilters ? 'Ninguna sesión en ese rango de fechas.' : 'Aún no hay sesiones OPU.'}
                    </p>
                    {!hasFilters && (
                        <Gate domain="aspirations" action="write">
                            <Link to="/admin/opu/new" className="cat-overview__add">
                                <IconPlus /><span>Crear la primera OPU</span>
                            </Link>
                        </Gate>
                    )}
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead>
                            <tr>
                                <th>Código</th>
                                <th>Fecha</th>
                                <th>Finca</th>
                                <th className="opu-col-timeline">Avance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map((s) => (
                                <tr key={s.id} className="cat-table__row">
                                    <td><Link to={`/admin/opu/${s.id}`} className="cat-table__name">{s.code}</Link></td>
                                    <td>{formatDateOnly(s.date) || <span className="cat-table__empty">—</span>}</td>
                                    <td>
                                        <div className="opu-cell-farm">
                                            <span className="opu-cell-farm__name">{s.farm?.name || '—'}</span>
                                            <span className="opu-cell-farm__asp">Asp. {aspiradorName(s) || '—'}</span>
                                        </div>
                                    </td>
                                    <td className="opu-col-timeline">
                                        <SessionTimeline item={s} onStep={(stepKey) => runStep(s, stepKey)} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
